import { CommandLineIcon } from '@heroicons/react/24/outline';
import { Badge, Box, Group, Title } from '@mantine/core';
import { TechnologyExperience } from '../../../models/technologyExperience.model';

interface CVSkillsSectionProps {
  skills: string[];
  technologyExperience: TechnologyExperience[];
}

export function CVSkillsSection({ skills, technologyExperience }: CVSkillsSectionProps) {
  if ((!skills || skills.length === 0) && (!technologyExperience || technologyExperience.length === 0)) {
    return null;
  }
  if (!skills || skills.length === 0)
    skills = technologyExperience.map(exp => exp.technologyName);
  if (!technologyExperience || technologyExperience.length === 0)
    technologyExperience = []

  // Create a map of technology names to their experience
  const experienceMap = new Map(
    technologyExperience.map(exp => [exp.technologyName, exp.yearsOfExperience])
  );

  // Separate skills into those with experience and those without
  const skillsWithExperience = skills
    .filter(tech => experienceMap.has(tech))
    .map(tech => ({
      name: tech,
      experience: experienceMap.get(tech)!
    }))
    .sort((a, b) => b.experience - a.experience);

  const skillsWithoutExperience = skills
    .filter(tech => !experienceMap.has(tech))
    .sort();

  // Combine: skills with experience first, then skills without
  const sortedSkills = [
    ...skillsWithExperience.map(s => ({ name: s.name, experience: s.experience })),
    ...skillsWithoutExperience.map(s => ({ name: s, experience: null }))
  ];

  // Colors for top 3 skills
  const topColors = ['violet', 'blue', 'cyan'];

  return (
    <Box>
      <Title order={5} mb="xs" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <CommandLineIcon style={{ width: 16, height: 16 }} />
        Key Skills
      </Title>
      <Box my="md">
        <Group gap={8}>
          {sortedSkills.map((skill, index) => {
            const isTopSkill = skill.experience !== null && index < 3;
            const badgeColor = isTopSkill ? topColors[index] : 'gray';

            // Size hierarchy: lg for top 3, sm for next 2, xs for rest
            let badgeSize: 'lg' | 'sm' | 'xs' = 'xs';
            if (skill.experience !== null) {
              if (index < 3) {
                badgeSize = 'lg';
              } else if (index < 5) {
                badgeSize = 'sm';
              }
            }

            return (
              <Badge
                key={index}
                variant="filled"
                color={badgeColor}
                size={badgeSize}
                radius="sm"
              >
                {skill.name}
                {skill.experience !== null && ` (${parseFloat(skill.experience.toFixed(2))}y)`}
              </Badge>
            );
          })}
        </Group>
      </Box>
    </Box>
  );
}

